import type { PostId } from '../../types/pifster-contracts';
import { isLoggedIn } from '../shared/auth-state';

type VoteStateMap = Record<string, number>;

export interface VoteStateService {
  readonly isLoggedIn: () => boolean;
  readonly hasPeriodVote: (periodId: PostId) => boolean;
  readonly markPeriodVote: (periodId: PostId) => void;
  readonly resetButtonLock: (button: HTMLButtonElement) => void;
}

function resolvePeriodKey(periodId: PostId): string {
  return `period:${String(periodId)}`;
}

function readVoteState(): VoteStateMap {
  try {
    const raw = window.sessionStorage.getItem('pifsterVotedPeriods');
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }

    return parsed as VoteStateMap;
  } catch {
    return {};
  }
}

function writeVoteState(map: VoteStateMap): void {
  window.sessionStorage.setItem('pifsterVotedPeriods', JSON.stringify(map));
}

export function createVoteStateService(): VoteStateService {
  return {
    isLoggedIn,
    hasPeriodVote: (periodId: PostId): boolean => {
      try {
        return readVoteState()[resolvePeriodKey(periodId)] !== undefined;
      } catch {
        return false;
      }
    },
    markPeriodVote: (periodId: PostId): void => {
      try {
        const map = readVoteState();
        map[resolvePeriodKey(periodId)] = Date.now();
        writeVoteState(map);
      } catch {
        // ignore storage errors
      }
    },
    resetButtonLock: (button: HTMLButtonElement): void => {
      delete button.dataset['clickLocked'];
      button.removeAttribute('aria-disabled');
    },
  };
}
